/**
 * Pure idle-session arithmetic (brief `tasks/prompt-auto-logout-dashboard.md`).
 * Everything takes `now` and the stored marks as arguments so it can be
 * unit-tested without timers or storage.
 */
import {
  ACTIVE_WINDOW_MAX_MS,
  DEFAULT_IDLE_TIMEOUT_MS,
  FALLBACK_KEEPALIVE_MS,
  KEEPALIVE_CHECK_MS,
  WARNING_LEAD_MS,
} from '@shared/lib/idleSession';

export const IDLE_PHASE = {
  active: 'active',
  warning: 'warning',
  expired: 'expired',
};

/** The API's idle length when it sent one, else the default. */
export const resolveIdleTimeoutMs = storedMs =>
  storedMs && storedMs > 0 ? storedMs : DEFAULT_IDLE_TIMEOUT_MS;

/** "Active" window for keepalive and pollers: 5 minutes, capped by the timeout. */
export const activeWindowMs = idleTimeoutMs => Math.min(ACTIVE_WINDOW_MAX_MS, idleTimeoutMs);

/**
 * Where the session stands relative to the last interaction.
 * `remainingMs` is the time left until the idle logout. The warning
 * never takes more than half of a short (test) timeout, or a one-minute
 * timeout would open the dialog on every interaction.
 */
export const idlePhase = ({
  now,
  lastInteractionAt,
  idleTimeoutMs,
  warningLeadMs = WARNING_LEAD_MS,
}) => {
  const last = lastInteractionAt ?? now;
  const remainingMs = last + idleTimeoutMs - now;
  if (remainingMs <= 0) return { phase: IDLE_PHASE.expired, remainingMs: 0 };
  if (remainingMs <= Math.min(warningLeadMs, Math.floor(idleTimeoutMs / 2))) {
    return { phase: IDLE_PHASE.warning, remainingMs };
  }
  return { phase: IDLE_PHASE.active, remainingMs };
};

export const isUserActive = ({ now, lastInteractionAt, idleTimeoutMs }) =>
  lastInteractionAt !== null &&
  lastInteractionAt !== undefined &&
  now - lastInteractionAt < activeWindowMs(idleTimeoutMs);

/**
 * Rule 3 of the brief: while the user is active, one metadata GET once the
 * JWT deadline has passed (the request itself does the exchange). Without
 * a deadline (old API) a fixed cadence. Never more often than the check
 * interval, so several tabs do not burst.
 */
export const shouldKeepAlive = ({
  now,
  lastInteractionAt,
  idleTimeoutMs,
  tokenDeadlineAt,
  lastKeepaliveAt,
}) => {
  if (!isUserActive({ now, lastInteractionAt, idleTimeoutMs })) return false;
  const sinceLast =
    lastKeepaliveAt === null || lastKeepaliveAt === undefined ? Infinity : now - lastKeepaliveAt;
  if (sinceLast < KEEPALIVE_CHECK_MS) return false;
  if (tokenDeadlineAt !== null && tokenDeadlineAt !== undefined) return now >= tokenDeadlineAt;
  return sinceLast >= FALLBACK_KEEPALIVE_MS;
};
